import { rem } from '@mantine/core';
import { useChangelog } from '../api/hooks';
import type { ChangelogEntry } from '../api/types';
import { useUi } from '../state/ui';
import { CARD, MONO, SECTION_LABEL } from './ui';
import { actorLabel, useCoordinatorLabel } from '../lib/people';

/** kind → Slovenian entry title (subset used for volunteer entries). */
function entryTitle(e: ChangelogEntry): string {
  const map: Record<string, string> = {
    created: 'Nov prostovoljec',
    updated: 'Urejen profil',
    deactivated: 'Izbris prostovoljca',
    cancelled: 'Ne pride',
    uncancelled: 'Vendarle pride',
    'confirmation-set': 'Potrditev',
    'shift-answer-set': 'Odgovor na izmeno',
    'tags-updated': 'Oznake urejene',
    'token-issued': 'Povezava do brifinga',
    'token-rotated': 'Nova povezava do brifinga',
    'briefing-published': 'Razpored objavljen brez pošte',
    'briefing-publish-skipped': 'Razpored ni bil objavljen',
    'briefing-sent': 'Napotnica poslana',
    'mail-uvodno': 'Uvodno pismo poslano',
    'mail-napotnica': 'Napotnica poslana',
    'mail-sprememba': 'Obvestilo o spremembi poslano',
    'mail-zahvala': 'Zahvala poslana',
    'mail-obvestilo': 'Obvestilo poslano',
    'mail-dnevno': 'Dnevno sporočilo poslano',
    'version-bumped': 'Nova različica razporeda',
    'note-added': 'Opomba med ekipama',
  };
  return map[e.kind] ?? e.kind;
}

export function HistoryTab({ volunteerId }: { volunteerId: string }) {
  const query = useChangelog('volunteer', volunteerId);
  const labelOf = useCoordinatorLabel();
  const { setChangelogOpen } = useUi();
  const rows = query.data?.entries ?? [];

  return (
    <div style={{ ...CARD, padding: '12px 12px 10px', maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span style={SECTION_LABEL}>SPREMEMBE, KI ZADEVAJO TEGA PROSTOVOLJCA</span>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setChangelogOpen(true)}
          style={{
            border: 0,
            background: 'none',
            cursor: 'pointer',
            fontSize: rem(12),
            fontFamily: 'inherit',
            color: 'oklch(0.5 0.09 245)',
            padding: 0,
          }}
        >
          celoten dnevnik ↗
        </button>
      </div>
      {query.isPending ? (
        <div style={{ padding: '14px', fontSize: rem(13), color: 'oklch(0.6 0.012 90)' }}>Nalagam…</div>
      ) : null}
      {rows.map((e) => (
        <div
          key={e.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '6px 12px 6px 10px',
            borderLeft: `2px solid ${e.breaking ? 'oklch(0.62 0.15 25)' : 'oklch(0.92 0.006 95)'}`,
            background: e.breaking ? 'oklch(0.995 0.008 25)' : 'oklch(1 0 0)',
          }}
        >
          <span
            style={{
              fontFamily: MONO,
              fontSize: rem(11.5),
              color: 'oklch(0.6 0.012 90)',
              flexShrink: 0,
              width: 86,
              whiteSpace: 'nowrap',
            }}
          >
            {new Date(e.createdAt).toLocaleString('sl-SI', {
              day: 'numeric',
              month: 'numeric',
              hour: '2-digit',
              hourCycle: 'h23',
              minute: '2-digit',
            })}
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: rem(13),
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <span style={{ fontWeight: 600, color: 'oklch(0.42 0.012 90)' }}>
              {actorLabel(e, labelOf)}
            </span>
            <span style={{ color: 'oklch(0.78 0.008 95)' }}> · </span>
            <span style={{ fontWeight: 600, color: e.breaking ? 'oklch(0.5 0.13 25)' : 'oklch(0.32 0.012 90)' }}>
              {entryTitle(e)}
            </span>
            {e.entityLabel ? (
              <span style={{ color: 'oklch(0.52 0.012 90)', fontFamily: MONO, fontSize: rem(11.5) }}>
                {' '}
                {e.entityLabel}
              </span>
            ) : null}
          </span>
        </div>
      ))}
      {!query.isPending && rows.length === 0 ? (
        <div style={{ padding: '22px 14px', fontSize: rem(13), color: 'oklch(0.6 0.012 90)' }}>
          Za tega prostovoljca še ni zabeleženih sprememb.
        </div>
      ) : null}
    </div>
  );
}
